import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageCircle, Send, Pencil, Trash2, Check, X as XIcon, BadgeCheck, Store, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import ReportDialog from "@/components/ReportDialog";

/**
 * Collapsible comment thread for a marketplace product.
 *
 * Rules:
 *  - Anyone signed-in can READ.
 *  - Only buyers (owners) or the seller themselves can POST (backend enforces).
 *  - Only the comment author can edit/delete their own.
 */
export default function ProductComments({ product, canComment, onCountChange, forceOpen }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(!!forceOpen);

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(product.comment_count || 0);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [reportId, setReportId] = useState(null);
  const loadedRef = useRef(false);

  const bumpCount = useCallback((n) => {
    setCount(n);
    onCountChange?.(product.product_id, n);
  }, [onCountChange, product.product_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/products/${product.product_id}/comments`);
      setComments(r.data.comments || []);
      bumpCount(r.data.count || 0);
      loadedRef.current = true;
    } catch {
      toast.error("Couldn't load comments");
    } finally {
      setLoading(false);
    }
  }, [product.product_id, bumpCount]);

  useEffect(() => {
    if (open && !loadedRef.current) load();
  }, [open, load]);

  const toggle = () => setOpen((v) => !v);

  const post = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const r = await api.post(`/products/${product.product_id}/comments`, { text });
      setComments((prev) => [...prev, r.data]);
      bumpCount(count + 1);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post comment");
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.comment_id);
    setEditDraft(c.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const saveEdit = async (c) => {
    const text = editDraft.trim();
    if (!text || text === c.text) {
      cancelEdit();
      return;
    }
    try {
      const r = await api.patch(`/products/${product.product_id}/comments/${c.comment_id}`, { text });
      setComments((prev) => prev.map((x) => x.comment_id === c.comment_id ? { ...x, text: r.data.text, updated_at: r.data.updated_at } : x));
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update comment");
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/products/${product.product_id}/comments/${c.comment_id}`);
      setComments((prev) => prev.filter((x) => x.comment_id !== c.comment_id));
      bumpCount(Math.max(0, count - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete comment");
    }
  };

  const initials = (name) => (name || "?").trim().slice(0, 1).toUpperCase();
  const canPostHere = !!user && canComment;

  return (
    <div className="pt-2">
      <button
        type="button"
        data-testid={`comments-toggle-${product.product_id}`}
        onClick={toggle}
        className="inline-flex items-center gap-1.5 text-[11px] text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
      >
        <MessageCircle className="h-3.5 w-3.5" />
        {count} comment{count === 1 ? "" : "s"}
        <span className="text-[#6B7280]">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div
          data-testid={`comments-panel-${product.product_id}`}
          className="mt-2 rounded-xl border border-[#1E1E1E] bg-[#0A0A0A]/60 p-3 space-y-3"
        >
          {loading ? (
            <div className="flex items-center justify-center py-4 text-[#6B7280] text-xs gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : comments.length === 0 ? (
            <div className="text-[11px] text-[#6B7280] text-center py-3">
              No comments yet. {canPostHere ? "Be the first!" : "Only buyers can comment."}
            </div>
          ) : (
            <div className="space-y-2.5">
              {comments.map((c) => (
                <div
                  key={c.comment_id}
                  data-testid={`comment-${c.comment_id}`}
                  className="flex items-start gap-2"
                >
                  {c.user_picture ? (
                    <img src={c.user_picture} alt="" className="h-6 w-6 rounded-full object-cover border border-[#27272A]" />
                  ) : (
                    <div className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
                      {initials(c.user_name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-[#F3F4F6] truncate">{c.user_name}</span>
                      {c.is_seller && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[#2563EB]/15 border border-[#2563EB]/30 text-[#3B82F6]">
                          <Store className="h-2.5 w-2.5" /> Seller
                        </span>
                      )}
                      {c.is_buyer && !c.is_seller && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981]">
                          <BadgeCheck className="h-2.5 w-2.5" /> Buyer
                        </span>
                      )}
                      {c.updated_at && <span className="text-[9px] text-[#6B7280]">(edited)</span>}
                    </div>
                    {editingId === c.comment_id ? (
                      <div className="mt-1 space-y-1.5">
                        <textarea
                          data-testid={`comment-edit-${c.comment_id}`}
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          rows={2}
                          maxLength={1000}
                          className="w-full resize-none rounded-lg bg-[#121212] border border-[#27272A] text-[12px] text-[#F3F4F6] px-2 py-1.5 focus:outline-none focus:border-[#3B82F6]"
                        />
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={cancelEdit}
                            data-testid={`comment-edit-cancel-${c.comment_id}`}
                            className="text-[10px] px-2 py-1 rounded-full border border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6]"
                          >
                            <XIcon className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => saveEdit(c)}
                            data-testid={`comment-edit-save-${c.comment_id}`}
                            className="text-[10px] px-2 py-1 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white inline-flex items-center gap-1"
                          >
                            <Check className="h-3 w-3" /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-[12px] text-[#D1D5DB] whitespace-pre-wrap break-words leading-snug">
                          {c.text}
                        </div>
                        {c.is_mine ? (
                          <div className="mt-1 flex items-center gap-1">
                            <button
                              onClick={() => startEdit(c)}
                              data-testid={`comment-edit-btn-${c.comment_id}`}
                              className="text-[10px] text-[#6B7280] hover:text-[#F3F4F6] inline-flex items-center gap-0.5"
                            >
                              <Pencil className="h-2.5 w-2.5" /> Edit
                            </button>
                            <button
                              onClick={() => remove(c)}
                              data-testid={`comment-delete-${c.comment_id}`}
                              className="text-[10px] text-[#6B7280] hover:text-[#EF4444] inline-flex items-center gap-0.5"
                            >
                              <Trash2 className="h-2.5 w-2.5" /> Delete
                            </button>
                          </div>
                        ) : user ? (
                          <div className="mt-1">
                            <button
                              onClick={() => setReportId(c.comment_id)}
                              data-testid={`comment-report-${c.comment_id}`}
                              className="text-[10px] text-[#6B7280] hover:text-[#EF4444] inline-flex items-center gap-0.5"
                            >
                              <Flag className="h-2.5 w-2.5" /> Report
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canPostHere ? (
            <div className="flex items-end gap-2 pt-1">
              <textarea
                data-testid={`comment-draft-${product.product_id}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a comment…"
                rows={1}
                maxLength={1000}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    post();
                  }
                }}
                className="flex-1 resize-none rounded-xl bg-[#121212] border border-[#27272A] text-[12px] text-[#F3F4F6] px-3 py-2 focus:outline-none focus:border-[#3B82F6]"
              />
              <button
                onClick={post}
                disabled={!draft.trim() || posting}
                data-testid={`comment-send-${product.product_id}`}
                className="h-9 w-9 shrink-0 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          ) : (
            <div className="text-[10px] text-[#6B7280] text-center">
              {user ? "Only buyers of this note can comment." : "Sign in to comment."}
            </div>
          )}
        </div>
      )}
      <ReportDialog
        open={!!reportId}
        onOpenChange={(o) => !o && setReportId(null)}
        targetType="comment"
        targetId={reportId}
      />
    </div>
  );
}
